// MUL-3919 (2026-09-22): Phase-3 subcontractor agreement status vocabulary.
// Defined here in `packages/shared` so the API lane (apps/api), the sub-onboarding
// app (apps/sub-onboarding) and the staff web (apps/web) all import the exact
// same strings — nobody redeclares the values.
//
// Source of truth: `docs/superpowers/specs/2026-09-22-sub-agreement-phase3-completion-contract.md`
// §1 "Shared vocabulary". If code and that document disagree, fix one of them in
// the same PR. Never let them drift silently.

// `PaymentTermsDays` is owned by `negotiable-terms.ts` and re-exported there
// as the canonical type. We re-export it here for the agreement-status surface
// so consumers can import a single module. The wildcard `export *` in
// `index.ts` would clash (TS2308) so we use a named re-export instead.
import type { PaymentTermsDays } from './negotiable-terms.js';
export type { PaymentTermsDays };

/** SubOnboardingSubmission.status values this feature reads or writes. */
export type SubmissionAgreementStatus =
  | 'submitted'                        // standard path, pre-issuance (legacy)
  | 'pending_terms_review'             // negotiated request awaiting a decision
  | 'terms_rejected'                   // decision = reject; sub may accept standard
  | 'approved_pending_issue'           // decision = approve; issuance row exists, worker not done
  | 'standard_accepted_pending_issue'  // sub accepted standard after a rejection
  | 'docusign_sent'
  | 'docusign_sub_signed'
  | 'docusign_signed'
  | 'docusign_declined'               // sub or counter-signer declined in DocuSign
  | 'docusign_voided';                // envelope voided in DocuSign

/** MsaEnvelopeIssuance.status */
export type IssuanceStatus =
  | 'pending'
  | 'issuing'
  | 'reconciling'
  | 'issued'
  | 'retryable_failed'
  | 'terminal_failed';

/** SubAgreementNegotiation.status */
export type NegotiationStatus = 'pending' | 'approved' | 'rejected';

/**
 * What the subcontractor status page renders. Derived server-side (§3.1 of
 * the contract), never computed in the browser from raw columns.
 *
 * - `pending_terms_review`   — HTS is reviewing a payment-terms request
 * - `terms_rejected`          — HTS rejected; sub may accept standard
 * - `confirming_issue`        — approved + issuance pending/issuing/reconciling
 * - `issue_delayed`           — issuance failed (retryable or terminal); ONE neutral state
 * - `ready_to_sign`           — DocuSign sent; sub signs (existing flow)
 * - `signed_pending_hts`      — sub signed; waiting for HTS counter-signature
 * - `complete`                — fully executed
 * - `agreement_cancelled`     — envelope declined or voided; HTS follows up
 */
export type SubAgreementPublicState =
  | 'pending_terms_review'
  | 'terms_rejected'
  | 'confirming_issue'
  | 'issue_delayed'
  | 'ready_to_sign'
  | 'signed_pending_hts'
  | 'complete'
  | 'agreement_cancelled';

export type AgreementApprovalFilter =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'issuance_attention';

/** Public, subcontractor-facing status block returned by GET /status/:accessToken. */
export interface PublicAgreementStatus {
  state: SubAgreementPublicState;
  /** What the subcontractor requested (the value from the wizard, even if rejected). */
  requestedPaymentTermsDays: PaymentTermsDays;
  /** Final term after a decision; undefined until decided. */
  finalPaymentTermsDays?: PaymentTermsDays | undefined;
  /** True only when the sub may accept standard terms (terms_rejected). */
  canAcceptStandard: boolean;
  /** Stable support reference; `HTS-` + first 8 chars of submission id, uppercased. */
  referenceId: string;
}

/** Extended /status response — `agreement` is null when neither negotiation nor issuance exists. */
export interface SubAgreementStatusResponse {
  status: SubmissionAgreementStatus;
  companyName: string;
  submittedAt: string;
  reviewedAt: string | null;
  docusignSentAt: string | null;
  docusignSignedAt: string | null;
  canOpenAgreement: boolean;
  agreement: PublicAgreementStatus | null;
}

/**
 * Map every (SubmissionAgreementStatus, [IssuanceStatus]) pair to the single
 * SubAgreementPublicState the subcontractor status page should render. Server
 * derives this; the UI must NOT recompute it.
 */
export function deriveSubAgreementPublicState(
  submission: SubmissionAgreementStatus,
  issuance: IssuanceStatus | null,
): SubAgreementPublicState {
  switch (submission) {
    case 'pending_terms_review':
      return 'pending_terms_review';
    case 'terms_rejected':
      return 'terms_rejected';
    case 'approved_pending_issue':
    case 'standard_accepted_pending_issue':
      if (
        issuance === 'reconciling' ||
        issuance === 'issuing' ||
        issuance === 'pending'
      ) {
        return 'confirming_issue';
      }
      if (issuance === 'retryable_failed' || issuance === 'terminal_failed') {
        return 'issue_delayed';
      }
      // approved + no issuance yet: still confirming.
      return 'confirming_issue';
    case 'docusign_sent':
      return 'ready_to_sign';
    case 'docusign_sub_signed':
      return 'signed_pending_hts';
    case 'docusign_signed':
      return 'complete';
    case 'docusign_declined':
    case 'docusign_voided':
      return 'agreement_cancelled';
    case 'submitted':
      // Legacy standard path before docusign_sent; render as confirming so
      // the sub sees progress copy instead of a stale "submitted" badge.
      return 'confirming_issue';
    default:
      return 'confirming_issue';
  }
}

/** Minimum/maximum length on the request reason (UI + server both use these). */
export const REQUEST_REASON_MIN_CHARS = 10;
export const REQUEST_REASON_MAX_CHARS = 1000;

/** Trim, then validate length. Server still re-validates; this keeps the UI honest. */
export function validateRequestReason(input: string): {
  ok: boolean;
  trimmed: string;
  message?: string;
} {
  const trimmed = (input ?? '').trim();
  if (trimmed.length < REQUEST_REASON_MIN_CHARS) {
    return {
      ok: false,
      trimmed,
      message: `Please give us at least ${REQUEST_REASON_MIN_CHARS} characters so we can review your request.`,
    };
  }
  if (trimmed.length > REQUEST_REASON_MAX_CHARS) {
    return {
      ok: false,
      trimmed,
      message: `Please keep this under ${REQUEST_REASON_MAX_CHARS} characters.`,
    };
  }
  return { ok: true, trimmed };
}